import { useState, useEffect, useRef } from "react";
import { cn } from "../../utils/tailwindUtils";
import {
    Flame, Zap, Snowflake, AlertTriangle, Clock, Flag,
    AlertCircle, TrendingDown, Phone, Mail, MessageSquare,
    FileText, Activity, RefreshCw, MessageCircle,
} from "lucide-react";
import { Doughnut, Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    ArcElement, CategoryScale, LinearScale,
    PointElement, LineElement,
    Tooltip, Legend, Filler,
} from "chart.js";
import useApiMethods from "../../lib/useApiMethods";
import { CUSTOMER_ENGAGEMENT_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import dayjs from "dayjs";

ChartJS.register(
    ArcElement, CategoryScale, LinearScale,
    PointElement, LineElement,
    Tooltip, Legend, Filler
);

// ── helpers ───────────────────────────────────────────────────────────────────

const GRADE_CONFIG = {
    Hot:  { icon: Flame,     color: "#ef4444", bg: "bg-red-50 dark:bg-red-900/20",    text: "text-red-600 dark:text-red-400",    ring: "#ef4444" },
    Warm: { icon: Zap,       color: "#f59e0b", bg: "bg-amber-50 dark:bg-amber-900/20", text: "text-amber-600 dark:text-amber-400", ring: "#f59e0b" },
    Cold: { icon: Snowflake, color: "#3b82f6", bg: "bg-blue-50 dark:bg-blue-900/20",   text: "text-blue-600 dark:text-blue-400",   ring: "#3b82f6" },
};

const SEVERITY_STYLE = {
    high:   "border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300",
    medium: "border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300",
};

const ALERT_ICON = {
    no_contact:        Clock,
    stuck_stage:       Flag,
    overdue_followup:  AlertCircle,
    low_score:         TrendingDown,
};

const BREAKDOWN_COLORS = ["#6366f1", "#22c55e", "#f59e0b", "#3b82f6", "#ec4899"];
const BREAKDOWN_ICON   = { Calls: Phone, Emails: Mail, Notes: MessageSquare, Documents: FileText, "CRM Events": Activity, WhatsApp: MessageCircle };

// ── GaugeChart ────────────────────────────────────────────────────────────────

const GaugeChart = ({ score, grade }) => {
    const cfg = GRADE_CONFIG[grade] || GRADE_CONFIG.Cold;
    const data = {
        datasets: [{
            data: [score, 100 - score],
            backgroundColor: [cfg.color, "#e5e7eb"],
            borderWidth: 0,
            circumference: 180,
            rotation: -90,
        }],
    };
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "75%",
        plugins: { legend: { display: false }, tooltip: { enabled: false } },
    };

    return (
        <div className="relative flex flex-col items-center">
            <div className="relative w-44 h-24">
                <Doughnut data={data} options={options} />
                {/* Centre text overlay */}
                <div className="absolute inset-0 flex flex-col items-center justify-end pb-1 pointer-events-none">
                    <span className={cn("text-3xl font-bold", cfg.text)}>{score}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 -mt-0.5">/ 100</span>
                </div>
            </div>
            {/* Grade badge */}
            <div className={cn("mt-1 flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold", cfg.bg, cfg.text)}>
                <cfg.icon className="h-4 w-4" />
                {grade} Engagement
            </div>
        </div>
    );
};

// ── BreakdownDonut ────────────────────────────────────────────────────────────

const BreakdownDonut = ({ breakdown }) => {
    const entries = Object.entries(breakdown).filter(([, v]) => v > 0);
    if (entries.length === 0) return (
        <div className="flex items-center justify-center h-32 text-xs text-gray-400 dark:text-gray-500">
            No activities recorded
        </div>
    );

    const data = {
        labels: entries.map(([k]) => k),
        datasets: [{
            data: entries.map(([, v]) => v),
            backgroundColor: BREAKDOWN_COLORS,
            borderWidth: 2,
            borderColor: "#fff",
        }],
    };
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "60%",
        plugins: {
            legend: { display: false },
            tooltip: { callbacks: { label: ctx => ` ${ctx.label}: ${ctx.raw}` } },
        },
    };

    return (
        <div className="flex items-center gap-4">
            <div className="w-28 h-28 flex-shrink-0">
                <Doughnut data={data} options={options} />
            </div>
            <div className="flex flex-col gap-1.5 flex-1">
                {entries.map(([label, count], i) => {
                    const Icon = BREAKDOWN_ICON[label] || Activity;
                    return (
                        <div key={label} className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1.5">
                                <div
                                    className="w-2 h-2 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: BREAKDOWN_COLORS[i % BREAKDOWN_COLORS.length] }}
                                />
                                <Icon className="h-3 w-3 text-gray-400" />
                                <span className="text-xs text-gray-600 dark:text-gray-400">{label}</span>
                            </div>
                            <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">{count}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ── ActivityTrend ─────────────────────────────────────────────────────────────

const ActivityTrend = ({ dailyTrend }) => {
    // Show every 5th label to avoid clutter
    const labels = dailyTrend.map((t, i) => {
        const d = dayjs(t.date);
        return i % 5 === 0 ? d.format("DD MMM") : "";
    });

    const data = {
        labels,
        datasets: [{
            label: "Activities",
            data: dailyTrend.map(t => t.count),
            borderColor: "#6366f1",
            backgroundColor: "rgba(99,102,241,0.12)",
            fill: true,
            tension: 0.4,
            pointRadius: (ctx) => (ctx.raw > 0 ? 3 : 0),
            pointBackgroundColor: "#6366f1",
        }],
    };
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    title: (items) => dayjs(dailyTrend[items[0].dataIndex]?.date).format("DD MMM YYYY"),
                    label: (ctx) => ` ${ctx.raw} activit${ctx.raw === 1 ? "y" : "ies"}`,
                },
            },
        },
        scales: {
            y: { beginAtZero: true, ticks: { stepSize: 1, font: { size: 10 } }, grid: { color: "rgba(0,0,0,0.04)" } },
            x: { ticks: { font: { size: 10 } }, grid: { display: false } },
        },
    };

    const hasData = dailyTrend.some(t => t.count > 0);
    if (!hasData) return (
        <div className="flex items-center justify-center h-24 text-xs text-gray-400 dark:text-gray-500">
            No activity in the last 30 days
        </div>
    );

    return (
        <div className="h-28">
            <Line data={data} options={options} />
        </div>
    );
};

// ── RiskAlerts ────────────────────────────────────────────────────────────────

const RiskAlerts = ({ alerts }) => {
    if (!alerts || alerts.length === 0) return (
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
            <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xs">✓</span>
            </div>
            <p className="text-xs font-medium text-green-700 dark:text-green-300">
                No risk alerts — customer is actively engaged
            </p>
        </div>
    );

    return (
        <div className="space-y-2">
            {alerts.map((alert, i) => {
                const Icon = ALERT_ICON[alert.type] || AlertTriangle;
                return (
                    <div
                        key={i}
                        className={cn(
                            "flex items-start gap-2.5 px-3 py-2.5 rounded-lg border text-xs font-medium",
                            SEVERITY_STYLE[alert.severity] || SEVERITY_STYLE.medium
                        )}
                    >
                        <Icon className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                        <span>{alert.message}</span>
                        {alert.severity === "high" && (
                            <span className="ml-auto flex-shrink-0 px-1.5 py-0.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-[10px] font-bold uppercase tracking-wide">
                                High
                            </span>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

// ── Score Formula tooltip ─────────────────────────────────────────────────────

const ScoringInfo = () => (
    <details className="mt-1">
        <summary className="text-[10px] text-gray-400 dark:text-gray-500 cursor-pointer select-none hover:text-gray-500 dark:hover:text-gray-400">
            How is this calculated?
        </summary>
        <div className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-0.5 text-[10px] text-gray-500 dark:text-gray-400 pl-1">
            <span>📞 Call</span>        <span>+10 pts</span>
            <span>📧 Email</span>       <span>+8 pts</span>
            <span>💬 Note / Chat</span> <span>+6 pts</span>
            <span>💚 WhatsApp</span>    <span>+6 pts</span>
            <span>📄 Document</span>    <span>+5 pts</span>
            <span>🔄 CRM Event</span>   <span>+15 pts</span>
            <span>⏱ No activity / 7d</span> <span>−10 pts</span>
        </div>
    </details>
);

// ── main export ───────────────────────────────────────────────────────────────

const EngagementDashboard = ({ customerId }) => {
    const { fetch } = useApiMethods();
    const [data, setData]       = useState(null);
    const [loading, setLoading] = useState(true);
    const hasFetched            = useRef(false);

    const load = () => {
        setLoading(true);
        fetch(CUSTOMER_ENGAGEMENT_URL, { customer_id: customerId })
            .then(res => {
                if (res?.status === "success") setData(res.data);
                else ToastMsg("error", res?.message || "Failed to load engagement data");
            })
            .catch(() => ToastMsg("error", "Failed to load engagement data"))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (!hasFetched.current && customerId) {
            hasFetched.current = true;
            load();
        }
    }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

    if (loading) return (
        <div className="flex items-center justify-center h-48">
            <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary mx-auto" />
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Computing engagement score…</p>
            </div>
        </div>
    );

    if (!data) return null;

    return (
        <div className="space-y-4 pb-2">
            {/* ── header row: gauge + meta ── */}
            <div className="flex items-start gap-4 flex-wrap">
                {/* Gauge */}
                <div className="flex flex-col items-center gap-1 min-w-[180px]">
                    <p className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                        Engagement Score
                    </p>
                    <GaugeChart score={data.score} grade={data.grade} />
                    <ScoringInfo />
                </div>

                {/* Meta stats */}
                <div className="flex-1 min-w-[140px] space-y-2 mt-2">
                    <StatRow
                        label="Total Activities (30d)"
                        value={data.total_activities}
                        highlight
                    />
                    <StatRow
                        label="Last Activity"
                        value={data.last_activity_date
                            ? dayjs(data.last_activity_date).format("DD MMM YYYY")
                            : "Never"}
                    />
                    {data.days_inactive != null && (
                        <StatRow
                            label="Days Since Last Contact"
                            value={`${data.days_inactive} day${data.days_inactive !== 1 ? "s" : ""}`}
                            warn={data.days_inactive >= 14}
                        />
                    )}

                    {/* Refresh */}
                    <button
                        onClick={load}
                        className="flex items-center gap-1 text-[10px] text-gray-400 dark:text-gray-500 hover:text-sidebar-primary mt-1"
                    >
                        <RefreshCw className="h-3 w-3" /> Refresh
                    </button>
                </div>
            </div>

            <Divider label="Engagement Breakdown" />

            {/* ── donut breakdown ── */}
            <BreakdownDonut breakdown={data.breakdown} />

            <Divider label="Activity Trend — Last 30 Days" />

            {/* ── line chart ── */}
            <ActivityTrend dailyTrend={data.daily_trend} />

            <Divider label="Risk Alerts" />

            {/* ── alerts ── */}
            <RiskAlerts alerts={data.risk_alerts} />
        </div>
    );
};

// ── tiny helpers ──────────────────────────────────────────────────────────────

const StatRow = ({ label, value, highlight, warn }) => (
    <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] text-gray-500 dark:text-gray-400">{label}</span>
        <span className={cn(
            "text-xs font-semibold",
            warn      ? "text-red-500 dark:text-red-400" :
            highlight ? "text-indigo-600 dark:text-indigo-400" :
                        "text-gray-700 dark:text-gray-300"
        )}>
            {value}
        </span>
    </div>
);

const Divider = ({ label }) => (
    <div className="flex items-center gap-2">
        <div className="h-px flex-1 bg-gray-100 dark:bg-gray-700" />
        <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider whitespace-nowrap">
            {label}
        </span>
        <div className="h-px flex-1 bg-gray-100 dark:bg-gray-700" />
    </div>
);

export default EngagementDashboard;
